import {
  compilePositionResponsibilityGraph,
  proposeStablePositionId,
  type PositionResponsibilityGraph,
} from '../../packages/compass-effective-authorization/dist/index.js';
import type { Committee, NamedRecord, Position } from '../types';

export function compileConfiguredPositionGraph(
  positions: Position[],
  committees: NamedRecord[],
): PositionResponsibilityGraph {
  return compilePositionResponsibilityGraph(
    positions.map((position) => ({
      positionId: position.positionId,
      committeeId: position.committee,
      reportsToPositionId: position.reportsToPositionId,
    })),
    committees.map((committee) => committee.id),
  );
}

export function positionConfigurationErrors(
  positions: Position[],
  committees: Committee[],
): string[] {
  const errors: string[] = [];
  const seenNames = new Set<string>();
  for (const position of positions) {
    const name = position.name.trim();
    if (!name || seenNames.has(name)) errors.push(`Position name "${name || '(blank)'}" is missing or duplicated.`);
    seenNames.add(name);
  }
  const graph = compileConfiguredPositionGraph(positions, committees);
  for (const diagnostic of graph.diagnostics) {
    const at = diagnostic.positionId ? ` for ${diagnostic.positionId}` : '';
    const related = diagnostic.relatedPositionId ? ` (${diagnostic.relatedPositionId})` : '';
    errors.push(`${diagnostic.code}${at}${related}`);
  }

  // Organization Model Phase C: Council and District are independent
  // organizational-area vocabularies — a Position may only reference an
  // organizational area (committee) at its own level, and a committee may
  // only nest under a parent at its own level. This is enforced here (the
  // Settings save path) and in the Committees/Positions editors' own
  // dropdown filtering; it is deliberately NOT enforced as a hard
  // server-side throw in functions/src/userAccess.ts's trust boundary,
  // to avoid turning a pre-existing cross-level reference into a
  // production outage before a migration has had a chance to fix it.
  const levelOf = new Map(committees.map((c) => [c.id, c.organizationLevel ?? 'district']));
  for (const position of positions) {
    if (!position.committee) continue;
    const committeeLevel = levelOf.get(position.committee);
    const positionLevel = position.organizationLevel ?? 'district';
    if (committeeLevel && committeeLevel !== positionLevel) {
      errors.push(
        `Position "${position.name}" (${positionLevel}) references a ${committeeLevel}-level organizational area.`,
      );
    }
  }
  for (const committee of committees) {
    if (!committee.parentId) continue;
    const parentLevel = levelOf.get(committee.parentId);
    const ownLevel = committee.organizationLevel ?? 'district';
    if (parentLevel && parentLevel !== ownLevel) {
      errors.push(
        `Committee "${committee.name}" (${ownLevel}) is nested under a ${parentLevel}-level committee.`,
      );
    }
  }

  return [...new Set(errors)].sort();
}

export function newStablePositionId(displayName: string): string | null {
  return proposeStablePositionId(displayName);
}
